// pages/monitoring/AlertsPage.tsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAlerts, acknowledgeAlert } from '../../store/slices/monitoringSlice';
import { RootState, AppDispatch } from '../../store';
import { Alert } from '../../types/monitoring';

const AlertsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { alerts, loading, error } = useSelector((state: RootState) => state.monitoring);
    const [filterResolved, setFilterResolved] = useState<boolean>(false);

    useEffect(() => {
        dispatch(fetchAlerts());

        // Set up polling interval to refresh data
        const interval = setInterval(() => {
            dispatch(fetchAlerts());
        }, 60000); // Every minute

        return () => clearInterval(interval);
    }, [dispatch]);

    const handleAcknowledge = (alertId: string) => {
        dispatch(acknowledgeAlert(alertId));
    };

    const filteredAlerts = filterResolved
        ? alerts
        : alerts.filter(alert => !alert.resolved);

    // Group alerts by severity
    const criticalAlerts = filteredAlerts.filter(alert => alert.severity === 'critical');
    const warningAlerts = filteredAlerts.filter(alert => alert.severity === 'warning');
    const infoAlerts = filteredAlerts.filter(alert => alert.severity === 'info');

    if (loading && alerts.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Active Alerts</h1>
                <div className="flex items-center">
                    <label className="flex items-center text-gray-700 dark:text-gray-300">
                        <input
                            type="checkbox"
                            checked={filterResolved}
                            onChange={() => setFilterResolved(!filterResolved)}
                            className="mr-2 rounded border-gray-300 focus:ring-primary-500 h-4 w-4"
                        />
                        Show resolved alerts
                    </label>
                    <button
                        onClick={() => dispatch(fetchAlerts())}
                        className="ml-4 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <p>Error: {error}</p>
                </div>
            )}

            {filteredAlerts.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                    <p className="text-gray-600 dark:text-gray-400">No alerts to display.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Critical Alerts */}
                    {criticalAlerts.length > 0 && (
                        <div>
                            <h2 className="text-xl font-semibold mb-3 text-red-600 dark:text-red-400">Critical Alerts</h2>
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {criticalAlerts.map((alert) => (
                                        <AlertItem key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Warning Alerts */}
                    {warningAlerts.length > 0 && (
                        <div>
                            <h2 className="text-xl font-semibold mb-3 text-yellow-600 dark:text-yellow-400">Warning Alerts</h2>
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {warningAlerts.map((alert) => (
                                        <AlertItem key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Info Alerts */}
                    {infoAlerts.length > 0 && (
                        <div>
                            <h2 className="text-xl font-semibold mb-3 text-blue-600 dark:text-blue-400">Information Alerts</h2>
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {infoAlerts.map((alert) => (
                                        <AlertItem key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

interface AlertItemProps {
    alert: Alert;
    onAcknowledge: (id: string) => void;
}

const AlertItem: React.FC<AlertItemProps> = ({ alert, onAcknowledge }) => {
    return (
        <li className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
            <div className="flex items-start">
                <div className={`mt-1 flex-shrink-0 w-3 h-3 rounded-full ${alert.severity === 'critical' ? 'bg-red-500' :
                        alert.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></div>
                <div className="ml-3 flex-1">
                    <div className="flex justify-between">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{alert.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(alert.timestamp).toLocaleString()}
                        </p>
                    </div>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{alert.message}</p>
                    <div className="mt-2 flex justify-between items-center">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Container: {alert.containerName}
                        </p>
                        {!alert.resolved ? (
                            <button
                                onClick={() => onAcknowledge(alert.id)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200 dark:text-primary-100 dark:bg-primary-900 dark:hover:bg-primary-800"
                            >
                                Acknowledge
                            </button>
                        ) : (
                            <span className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded text-green-700 bg-green-100 dark:text-green-100 dark:bg-green-900">
                                Resolved
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
};

export default AlertsPage;